/**
 * Eligibility rules for "Cancel Send" on a bulk message — the UI face of the
 * backend `revertCadenceToDraft` mutation.
 *
 * These rules deliberately mirror `_revert_to_draft_block_reason` in
 * et-cadences (`cadences/graphql/mutation/cadence_mutation.py`). The server is
 * the authority and re-checks every one of them; this module exists so the menu
 * item can be greyed out with an explanation instead of failing on click. Keep
 * the two in sync — if the backend rules move, these strings are what users
 * will read.
 *
 * Why raw `status` and not the rendered status label: the backend gates on the
 * raw column (`_BULK_REVERTIBLE_STATUSES = (SCHEDULED, PAUSED)`), and a bulk
 * message's display label is derived from `displayStatus`, which tracks the
 * send lifecycle separately. Gating on the label would disagree with the server
 * — e.g. stage has `status='scheduled'` rows carrying `displayStatus='sent'`,
 * which read as "Sent" and must stay blocked.
 */

/** Matches MIN_REVERT_BUFFER_SECONDS in et-cadences. */
export const CANCEL_SEND_MIN_BUFFER_SECONDS = 60;

/** Raw cadence statuses a bulk message may be reverted to draft from. */
const CANCELLABLE_STATUSES = ['scheduled', 'paused'];

/** displayStatus values that mean the send has begun and can no longer be pulled back. */
const SEND_STARTED_DISPLAY_STATUSES = ['sending', 'sent'];

/**
 * Parse a datetime the cadences backend returned.
 *
 * Scheduled send times come back as naive UTC strings with no Z suffix, so a
 * bare `new Date(str)` would read them as browser-local time and shift the
 * comparison by the user's offset. Append Z when no offset is present — the
 * same normalization create-bulk-message-page.js does when rehydrating the
 * schedule form.
 *
 * @returns {Date|null} null when the value is missing or unparseable
 */
export const parseBackendUtcDate = (value) => {
  if (!value) return null;
  const raw = String(value);
  const normalized = raw.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`;
  const parsed = new Date(normalized);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Why "Cancel Send" is unavailable for this bulk message, or null if it's allowed.
 *
 * The returned string is shown to the user beneath the greyed-out menu item, so
 * it must read as an explanation rather than an error code.
 *
 * @param {object} message cadence with status, displayStatus, scheduledSendDate
 * @param {Date} [now] injectable clock for tests
 * @returns {string|null} block reason, or null when the send can be cancelled
 */
export const getCancelSendBlockReason = (message, now = new Date()) => {
  if (!message) return 'This bulk message is still loading.';

  const status = String(message.status || '').toLowerCase();
  const displayStatus = String(message.displayStatus || '').toLowerCase();

  // Checked before status: a scheduled-but-already-sent row exists on stage, and
  // the send having started outranks whatever the status column says.
  if (SEND_STARTED_DISPLAY_STATUSES.includes(displayStatus)) {
    return displayStatus === 'sent'
      ? 'This bulk message has already been sent.'
      : 'This bulk message is already sending.';
  }

  if (status === 'draft') return 'This bulk message is already a draft.';
  if (status === 'archived') return 'This bulk message is archived.';
  if (!CANCELLABLE_STATUSES.includes(status)) {
    return 'Only a scheduled or paused bulk message can be cancelled.';
  }

  const sendAt = parseBackendUtcDate(message.scheduledSendDate);
  if (sendAt) {
    const remainingMs = sendAt.getTime() - now.getTime();
    if (remainingMs <= 0) {
      return 'The scheduled send time has already passed.';
    }
    if (remainingMs <= CANCEL_SEND_MIN_BUFFER_SECONDS * 1000) {
      return `The scheduled send is less than ${CANCEL_SEND_MIN_BUFFER_SECONDS} seconds away — too close to cancel reliably.`;
    }
  }

  return null;
};

/** Body copy for the Cancel Send confirmation modal. */
export const buildCancelSendConfirmBody = (messageName) =>
  `This will stop the scheduled send for "${messageName}" and return it to draft. ` +
  'No messages will go out, and the scheduled send date and time are kept, so you can ' +
  'make your edits and publish again on the same schedule.';
